import { useEffect, useRef } from 'react';

import { isSameDay } from 'date-fns';

const ROLLOVER_CHECK_INTERVAL_MS = 30_000;

/**
 * Fires `onRollover(now, previous)` when the calendar day changes relative
 * to the previous check. Handles tabs left open across midnight: polls on an
 * interval and also re-checks whenever the tab regains focus or visibility,
 * so background-throttled timers don't miss the transition.
 *
 * `onRollover` is wrapped in a ref so callers don't need to memoize it.
 */
export function useDayRollover(onRollover: (now: Date, previous: Date) => void) {
  const callbackRef = useRef(onRollover);
  callbackRef.current = onRollover;

  const lastCheckRef = useRef<Date>(new Date());

  useEffect(() => {
    const check = () => {
      const now = new Date();
      const previous = lastCheckRef.current;
      if (!isSameDay(previous, now)) {
        lastCheckRef.current = now;
        callbackRef.current(now, previous);
        return;
      }
      lastCheckRef.current = now;
    };

    const intervalId = setInterval(check, ROLLOVER_CHECK_INTERVAL_MS);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);
}
